import { ArrowDown, ArrowUp, ChevronDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { surveyQuestionnairesApi } from '../surveyApi';
import { SurveyQuestionnairePreview } from './SurveyQuestionnairePreview';
import {
  SURVEY_QUESTION_TYPE,
  SURVEY_QUESTION_TYPE_LABELS,
  isChoiceType,
  type SurveyQuestionTypeValue,
  type SurveyQuestionnaire,
  type SurveyQuestionnaireQuestion,
  type SurveyQuestionnaireQuestionInput,
} from '../surveyTypes';
import { sampleSurveyQuestionnaire } from '../utils/sampleSurvey';

const ID = 'survey-questionnaire';

interface DraftOption {
  /** Stable across renders, so React keeps each input — and its caret — in place. */
  key: string;
  id: string | null;
  text: string;
}

interface DraftQuestion {
  key: string;
  id: string | null;
  text: string;
  description: string;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel: string;
  scaleMaxLabel: string;
  options: DraftOption[];
}

/*
  Name and Division are NO LONGER OFFERED (product call, 13 Oct 2026): the respondent is known
  from who answers, so a question asking for either is not one to add. A question ALREADY of
  either type keeps its own option, so an older questionnaire still reads as what it is.
*/
const RETIRED_TYPES: SurveyQuestionTypeValue[] = [SURVEY_QUESTION_TYPE.Name, SURVEY_QUESTION_TYPE.Division];

function typeOptionsFor(current: SurveyQuestionTypeValue) {
  return SURVEY_QUESTION_TYPE_LABELS.filter((t) => t.value === current || !RETIRED_TYPES.includes(t.value)).map((t) => ({
    value: String(t.value),
    label: t.label,
  }));
}
const SCALE_MIN_OPTIONS = [0, 1].map((n) => ({ value: String(n), label: String(n) }));
const SCALE_MAX_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ value: String(n), label: String(n) }));

function blankQuestion(): DraftQuestion {
  return {
    key: uid(),
    id: null,
    text: '',
    description: '',
    type: SURVEY_QUESTION_TYPE.MultipleChoice,
    isRequired: false,
    scaleMin: 1,
    scaleMax: 5,
    scaleMinLabel: '',
    scaleMaxLabel: '',
    options: [{ key: uid(), id: null, text: '' }],
  };
}

function toDraft(q: SurveyQuestionnaireQuestion | SurveyQuestionnaireQuestionInput): DraftQuestion {
  return {
    key: uid(),
    id: q.id,
    text: q.text,
    description: q.description ?? '',
    type: q.type,
    isRequired: q.isRequired,
    scaleMin: q.scaleMin,
    scaleMax: q.scaleMax,
    scaleMinLabel: q.scaleMinLabel ?? '',
    scaleMaxLabel: q.scaleMaxLabel ?? '',
    options: q.options.map((o) => ({ key: uid(), id: o.id, text: o.text })),
  };
}

/** Whether a question can be submitted — its text, and an option to choose where the type needs one. */
function questionIsComplete(q: DraftQuestion): boolean {
  return q.text.trim().length > 0 && (!isChoiceType(q.type) || q.options.some((o) => o.text.trim()));
}

/** The first thing a question still needs, in the words its own field error uses — or null. */
function questionProblem(q: DraftQuestion): string | null {
  if (!q.text.trim()) return 'Enter the question.';
  if (isChoiceType(q.type) && !q.options.some((o) => o.text.trim())) return 'Enter at least one option.';
  return null;
}

/**
 * Where the server's field names for question `i` land in this form, so a refusal focuses the
 * box to fix. Both casings: the service names `Questions[1].Text`, FluentValidation camel-cases.
 */
function questionFieldIds(count: number): Record<string, string> {
  const ids: Record<string, string> = {};

  for (let i = 0; i < count; i++) {
    const pairs: [string, string][] = [
      ['Text', `${ID}-question-${i}-text`],
      ['Description', `${ID}-question-${i}-description`],
      ['Type', `${ID}-question-${i}-type`],
      ['Options', `${ID}-question-${i}-option-0`],
      ['ScaleMin', `${ID}-question-${i}-scale-min`],
      ['ScaleMax', `${ID}-question-${i}-scale-max`],
      ['ScaleMinLabel', `${ID}-question-${i}-scale-min-label`],
      ['ScaleMaxLabel', `${ID}-question-${i}-scale-max-label`],
    ];

    for (const [field, id] of pairs) {
      ids[`Questions[${i}].${field}`] = id;
      ids[`questions[${i}].${field[0].toLowerCase()}${field.slice(1)}`] = id;
    }
  }

  return ids;
}

/**
 * Create or edit one questionnaire of a survey — its title, its description, and its questions.
 *
 * **The whole questionnaire is one form and one Submit**, so it is never half-saved and it goes
 * through the shared Modal and confirm tick like every other form. Each question takes a type
 * from the product owner's list — Multiple choice, Short paragraph, Long paragraph, Scale,
 * Checkbox, Dropdown, Date, Time — and the block below it draws what that type needs.
 */
export function SurveyQuestionnaireModal({
  open,
  surveyId,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  surveyId: string;
  /** Null creates. */
  editing: SurveyQuestionnaire | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [title, setTitle] = useState(editing?.title ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);

  // Which blocks are collapsed, by draft key. Not persisted: it is a view of this form, not data.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  // An edit opens on the questions it already has, so it waits for them.
  const [loading, setLoading] = useState(editing !== null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!editing) return;

    const controller = new AbortController();

    surveyQuestionnairesApi
      .get(surveyId, editing.id, controller.signal)
      .then((detail) => {
        if (controller.signal.aborted) return;

        setQuestions(detail.questions.map(toDraft));
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load this questionnaire.', 0));
        setLoading(false);
      });

    return () => controller.abort();
  }, [editing, surveyId]);

  /*
    LOCKED once answered: the server refuses any change to the questions of a questionnaire with a
    live response, because the answers point at them. The form says so, draws them read only and
    sends them back exactly as loaded; the title and description stay editable.
  */
  const locked = (editing?.responseCount ?? 0) > 0;

  // A Name question says WHO the response is for, so there is one or none.
  const nameCount = questions.filter((q) => q.type === SURVEY_QUESTION_TYPE.Name).length;

  const valid = title.trim().length > 0 && questions.every(questionIsComplete) && nameCount <= 1;
  const canSave = valid && confirmed && !saving && !loading && loadError === null;

  function updateQuestion(key: string, patch: Partial<DraftQuestion>) {
    setQuestions((current) => current.map((q) => (q.key === key ? { ...q, ...patch } : q)));
  }

  function changeType(question: DraftQuestion, value: string) {
    const type = Number(value) as SurveyQuestionTypeValue;

    // A choice question with nothing to choose from cannot be answered, so it opens with a row.
    updateQuestion(question.key, {
      type,
      options:
        isChoiceType(type) && question.options.length === 0 ? [{ key: uid(), id: null, text: '' }] : question.options,
    });
  }

  function moveQuestion(index: number, direction: -1 | 1) {
    setQuestions((current) => {
      const next = [...current];
      [next[index], next[index + direction]] = [next[index + direction], next[index]];
      return next;
    });
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      title: title.trim(),
      description: description.trim() || null,
      questions: questions.map((q) => ({
        id: q.id,
        text: q.text.trim(),
        description: q.description.trim() || null,
        type: q.type,
        isRequired: q.isRequired,
        scaleMin: q.scaleMin,
        scaleMax: q.scaleMax,
        scaleMinLabel: q.scaleMinLabel.trim() || null,
        scaleMaxLabel: q.scaleMaxLabel.trim() || null,
        // Sent only where the type uses them; another type's leftover options would be kept for nothing.
        options: isChoiceType(q.type) ? q.options.map((o) => ({ id: o.id, text: o.text.trim() })) : [],
      })),
    };

    try {
      if (editing) await surveyQuestionnairesApi.update(surveyId, editing.id, request);
      else await surveyQuestionnairesApi.create(surveyId, request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this questionnaire.', 0);

      setError(failure);

      // A refusal may name a field inside a collapsed block, which focus cannot reach — so every
      // block opens first, and the focus waits for that render.
      setCollapsed(new Set());
      window.requestAnimationFrame(() =>
        focusValidationError(failure, { idPrefix: ID, fieldIds: questionFieldIds(questions.length) }),
      );
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleSurveyQuestionnaire();

    setTitle(sample.title);
    setDescription(sample.description);

    // On an edit the questions already there are the reader's own, so samples are appended —
    // and a locked questionnaire's questions are not touched at all.
    if (!locked) {
      setQuestions((current) => [...(editing ? current : []), ...sample.questions.map(toDraft)]);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Names the questionnaire on an edit, so a reader who opened the wrong row sees it first.
      title={editing ? `Edit questionnaire — ${editing.title}` : 'New questionnaire'}
      // How many questions the form holds, as a badge beside the title (product call, 12 Oct 2026)
      // — it stays on screen while the questions scroll, and follows every add and remove.
      titleCount={questions.length}
      // Full screen, with the body's own scroll off: each half owns its scroll region, so the
      // preview stays beside the question being written however long the form grows.
      size="fullscreen"
      bodyScroll={false}
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loading || loadError !== null }}
      confirm={{
        id: `${ID}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="grid min-h-0 flex-1 grid-rows-2 gap-6 lg:grid-cols-2 lg:grid-rows-1">
        {/*
          A 4px inset, cancelled by the negative margin so the fields keep their edge: this column
          clips sideways, and a focus RING is drawn outside the input's box, so without the inset
          every field's ring would lose its left side.
        */}
        <div className="modern-scrollbar -mx-1 flex min-h-0 flex-col gap-4 overflow-y-auto overflow-x-hidden px-1 py-1">
          {error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">{error.message}</p>
            </div>
          ) : null}

          <TextField
            id={`${ID}-title`}
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={title.trim() ? error?.fieldError('title') : 'Enter a title.'}
            disabled={saving}
          />

          <Textarea
            id={`${ID}-description`}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={error?.fieldError('description')}
            disabled={saving}
          />

          <FormFieldset legend="Questions">
            {loadError ? (
              <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
            ) : loading ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {locked ? (
                  <p role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    This questionnaire has been answered, so its questions can no longer change. Its title and description still can.
                  </p>
                ) : null}

                {questions.length === 0 ? <p className="text-xs text-slate-500">No questions yet.</p> : null}

                {questions.map((question, index) => (
                  <QuestionBlock
                    key={question.key}
                    question={question}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === questions.length - 1}
                    disabled={saving || locked}
                    error={error}
                    secondName={
                      question.type === SURVEY_QUESTION_TYPE.Name &&
                      questions.findIndex((q) => q.type === SURVEY_QUESTION_TYPE.Name) < index
                    }
                    collapsed={collapsed.has(question.key)}
                    onToggle={() =>
                      setCollapsed((current) => {
                        const next = new Set(current);
                        if (next.has(question.key)) next.delete(question.key);
                        else next.add(question.key);
                        return next;
                      })
                    }
                    onChange={(patch) => updateQuestion(question.key, patch)}
                    onType={(value) => changeType(question, value)}
                    onMove={(direction) => moveQuestion(index, direction)}
                    onRemove={() => setQuestions((current) => current.filter((q) => q.key !== question.key))}
                  />
                ))}

                {locked ? null : (
                  <div>
                    <Button
                      id={`${ID}-add-question`}
                      variant="secondary"
                      onClick={() => setQuestions((current) => [...current, blankQuestion()])}
                      disabled={saving}
                    >
                      Add question
                    </Button>
                  </div>
                )}
              </div>
            )}
          </FormFieldset>
        </div>

        <div className="modern-scrollbar min-h-0 overflow-y-auto overflow-x-hidden rounded-xl bg-slate-100 p-5">
          <SurveyQuestionnairePreview title={title} description={description} questions={questions} />
        </div>
      </div>
    </Modal>
  );
}

/** One question of the form: its text, type and Required tick, then what the type needs. */
function QuestionBlock({
  question,
  index,
  isFirst,
  isLast,
  disabled,
  error,
  secondName,
  collapsed,
  onToggle,
  onChange,
  onType,
  onMove,
  onRemove,
}: {
  question: DraftQuestion;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  error: ApiError | null;
  /** This is a second Name question — the form refuses it, so the type picker says why. */
  secondName: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<DraftQuestion>) => void;
  onType: (value: string) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const prefix = `${ID}-question-${index}`;
  const field = (name: string) => error?.fieldError(`questions[${index}].${name}`);
  const iconButton =
    'rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-40';

  function setOption(key: string, text: string) {
    onChange({ options: question.options.map((o) => (o.key === key ? { ...o, text } : o)) });
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-2">
        {/*
          COLLAPSED keeps what names the block — its number and its text — so the reader can
          still tell the questions apart, and a block that is not finished says so in red here,
          because its field error is hidden with it.
        */}
        <p className="flex min-w-0 items-baseline gap-2 text-sm">
          <span className="shrink-0 font-medium text-slate-800">Question {index + 1}</span>
          {collapsed && question.text.trim() ? (
            <span className="truncate text-slate-500">— {question.text.trim()}</span>
          ) : null}
          {collapsed && (questionProblem(question) ?? secondName) ? (
            <span className="shrink-0 text-xs text-red-700">
              {questionProblem(question) ?? 'A questionnaire has at most one Name question.'}
            </span>
          ) : null}
        </p>

        <div className="flex shrink-0 items-center gap-1">
          <Tooltip label="Move up" disabledReason={isFirst ? 'This is already the first question.' : null}>
            <button
              type="button"
              onClick={() => onMove(-1)}
              disabled={disabled || isFirst}
              aria-label={`Move question ${index + 1} up`}
              className={iconButton}
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label="Move down" disabledReason={isLast ? 'This is already the last question.' : null}>
            <button
              type="button"
              onClick={() => onMove(1)}
              disabled={disabled || isLast}
              aria-label={`Move question ${index + 1} down`}
              className={iconButton}
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label="Remove">
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              aria-label={`Remove question ${index + 1}`}
              className={iconButton}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
          {/* Down while expanded, right while collapsed — one icon rotated, as everywhere. */}
          <Tooltip label={collapsed ? 'Expand' : 'Collapse'}>
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-controls={`${prefix}-body`}
              aria-label={`${collapsed ? 'Expand' : 'Collapse'} question ${index + 1}`}
              className={iconButton}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                aria-hidden="true"
              />
            </button>
          </Tooltip>
        </div>
      </div>

      {collapsed ? null : (
        <div id={`${prefix}-body`} className="flex flex-col gap-3">
          <TextField
            id={`${prefix}-text`}
            label="Question"
            required
            value={question.text}
            onChange={(e) => onChange({ text: e.target.value })}
            error={question.text.trim() ? field('text') : 'Enter the question.'}
            disabled={disabled}
          />

          <Textarea
            id={`${prefix}-description`}
            label="Description"
            value={question.description}
            onChange={(e) => onChange({ description: e.target.value })}
            error={field('description')}
            disabled={disabled}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              id={`${prefix}-type`}
              label="Type"
              required
              value={String(question.type)}
              onChange={onType}
              options={typeOptionsFor(question.type)}
              sort="none"
              searchable={false}
              clearable={false}
              disabled={disabled}
              error={secondName ? 'A questionnaire has at most one Name question.' : field('type')}
            />

            <label htmlFor={`${prefix}-required`} className="flex items-center gap-2.5 self-end pb-2.5">
              <Checkbox
                id={`${prefix}-required`}
                checked={question.isRequired}
                onChange={(e) => onChange({ isRequired: e.target.checked })}
                disabled={disabled}
              />
              <span className="text-sm text-slate-700">Required</span>
            </label>
          </div>

          {isChoiceType(question.type) ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-700">Options</p>

              {question.options.map((option, j) => (
                <div key={option.key} className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <TextField
                      id={`${prefix}-option-${j}`}
                      label={`Option ${j + 1}`}
                      labelHidden
                      placeholder={`Option ${j + 1}`}
                      value={option.text}
                      onChange={(e) => setOption(option.key, e.target.value)}
                      error={j === 0 && !question.options.some((o) => o.text.trim()) ? 'Enter at least one option.' : j === 0 ? field('options') : undefined}
                      disabled={disabled}
                    />
                  </div>

                  {/* The last option cannot go: a choice question needs something to choose. */}
                  {question.options.length > 1 ? (
                    <Tooltip label="Remove">
                      <button
                        type="button"
                        onClick={() => onChange({ options: question.options.filter((o) => o.key !== option.key) })}
                        disabled={disabled}
                        aria-label={`Remove option ${j + 1} of question ${index + 1}`}
                        className={`${iconButton} mt-1.5`}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  ) : null}
                </div>
              ))}

              <div>
                <Button
                  id={`${prefix}-add-option`}
                  variant="tertiary"
                  onClick={() => onChange({ options: [...question.options, { key: uid(), id: null, text: '' }] })}
                  disabled={disabled}
                >
                  Add option
                </Button>
              </div>
            </div>
          ) : null}

          {question.type === SURVEY_QUESTION_TYPE.Scale ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id={`${prefix}-scale-min`}
                label="From"
                value={String(question.scaleMin)}
                onChange={(v) => onChange({ scaleMin: Number(v) })}
                options={SCALE_MIN_OPTIONS}
                sort="none"
                searchable={false}
                clearable={false}
                disabled={disabled}
                error={field('scaleMin')}
              />
              <Select
                id={`${prefix}-scale-max`}
                label="To"
                value={String(question.scaleMax)}
                onChange={(v) => onChange({ scaleMax: Number(v) })}
                options={SCALE_MAX_OPTIONS}
                sort="none"
                searchable={false}
                clearable={false}
                disabled={disabled}
                error={field('scaleMax')}
              />
              <TextField
                id={`${prefix}-scale-min-label`}
                label={`Label for ${question.scaleMin}`}
                value={question.scaleMinLabel}
                onChange={(e) => onChange({ scaleMinLabel: e.target.value })}
                error={field('scaleMinLabel')}
                disabled={disabled}
              />
              <TextField
                id={`${prefix}-scale-max-label`}
                label={`Label for ${question.scaleMax}`}
                value={question.scaleMaxLabel}
                onChange={(e) => onChange({ scaleMaxLabel: e.target.value })}
                error={field('scaleMaxLabel')}
                disabled={disabled}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
